import * as Yup from "yup";
import { Formik, Form } from "formik";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router";
import { toastr } from "react-redux-toastr";
import { useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Button, TextField, Typography } from "@mui/material";
// component
import AuthService from "Lib/API/Auth";
import { useApiAction } from "hook/useFetchData";
import { getLocalStorageItem } from "utils/functions";
// direct import
import "./index.scss";
import { Fonts, FontFamilies, FontColors } from "theme/fontConfig";

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .email("Enter a valid email address")
    .required("Email is required"),
});

const Login = () => {
  const history = useHistory();
  // const orgName = getLocalStorageItem("orgName");
  const isSmallScreen = useMediaQuery("(max-width:699px)");
  const logo = getLocalStorageItem("getOrginasitionBySubDomain", true);
  // state
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    document.title = logo?.orgName || "Login";
  }, [logo]);

  const { execute: login } = useApiAction(
    (body) => AuthService.loginWithOtp(body),
    false
  );

  return (
    <><Helmet>
      <title>{logo?.orgName}</title>
    </Helmet>
  <Box className="login-wrapper">
        <Box
          sx={{
            width: {
              xs: "95%",
              sm: 400,
              md: 450,
            },
            boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
            backgroundColor: "white",
            p: 3,
            borderRadius: "5px",
          }}
        >
          <Box className="login-logo">
            {logo?.logoImg && (
              <img src={logo?.logoImg} alt="logo" loading="lazy" width={160} />
            )}
          </Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            align="center"
            sx={{ fontSize: isSmallScreen ? Fonts.GeneralSubHeaderFont.fontSize : Fonts.GeneralHeaderFont.fontSize }}
          >
            {logo?.orgName}
          </Typography>

          <Typography
            variant="body2"
            align="center"
            sx={{
              mt: 1,
              fontSize: isSmallScreen ? Fonts.GeneralBodyFont.fontSize : Fonts.GeneralLargeBodyFont.fontSize,
              color: FontColors.BlackAlpha54,
              mb: isSmallScreen ? 3 : 0,
            }}
          >
            Enter your email to sign in to your account
          </Typography>

          <Box
            sx={{
              p: {
                xs: 0,
                sm: 0,
                md: 2,
              },
            }}
          >
            <Formik
              initialValues={{ email: "" }}
              validationSchema={LoginSchema}
              onSubmit={async (values) => {
                try {
                  const res = await login({
                    email: values?.email,
                  });
                  toastr.success(
                    "Success",
                    "Otp sent successfully. Please check your email!"
                  );
                  setTimeout(() => {
                    history.push(
                      `/verify-otp/${res?.result?.token}?email=${res?.result?.email}&&sentMail=${encodeURIComponent(values?.email)}`
                    );
                  }, 100);
                } catch (error) {
                  setErrorMessage(error?.data?.message);
                  setTimeout(() => {
                    setErrorMessage(null);
                  }, 7000);
                } finally {
                }
              }}
            >
              {({
                values, errors, touched, handleBlur, isSubmitting, handleChange,
              }) => (
                <Form>
                  <Box
                    display="flex"
                    justifyContent="center"
                    sx={{
                      gap: {
                        xs: 0.5,
                        sm: 1,
                        md: 1.5,
                      },
                    }}
                    mb={2}
                  >
                    <TextField
                      name="email"
                      placeholder="yours@example.com"
                      variant="outlined"
                      fullWidth
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={(touched.email && Boolean(errors.email)) || errorMessage}
                      helperText={(touched.email && errors.email) || errorMessage}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontFamily: FontFamilies.General,
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: Fonts.GeneralCaptionFont.fontFamily,
                          fontSize: Fonts.GeneralCaptionFont.fontSize,
                          marginLeft: 0,
                        },
                      }} />
                  </Box>
                  <Box
                    sx={{
                      flexDirection: "column",
                      mt: 2,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      sx={{
                        borderRadius: 2,
                        px: 4,
                        textTransform: "none",
                        fontWeight: Fonts.GeneralSubHeaderFont.fontWeight,
                        backgroundColor: "#1675e0",
                        width: "100%",
                        fontSize: Fonts.GeneralSubHeaderFont.fontSize,
                      }}
                      disabled={isSubmitting}
                    >
                      Send Code
                      {isSubmitting && (
                        <CircularProgress
                          sx={{ color: FontColors.Blue1976D2, ml: 1 }}
                          size={15}
                          thickness={4} />
                      )}
                    </Button>
                  </Box>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      mt: 2,
                      fontSize: isSmallScreen ? Fonts.GeneralCaptionFont.fontSize : Fonts.GeneralLargeBodyFont.fontSize,
                      color: FontColors.BlackAlpha54,
                    }}
                  >
                    By logging in, you agree to our terms of service and privacy
                    policy.
                  </Typography>
                  {logo?.memberPortalApp && (
                    <Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.BlackAlpha45 }}
                      >
                        Also available on
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                        <a
                          href="https://apps.apple.com/il/app/easyshul-members/id6758013315"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                            alt="Download on the App Store"
                            height={36}
                          />
                        </a>
                        <a
                          href="https://play.google.com/store/apps/details?id=com.members.easyshul"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                            alt="Get it on Google Play"
                            height={53}
                          />
                        </a>
                      </Box>
                    </Box>
                  )}
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Box></>
  );
};

export default Login;
